/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { axios, baseUrl, endpoints } from 'Api';
import { AppContext, AuthContext } from 'Context';
import styles from './index.module.css';

const rowName = (u) => `${u?.firstname || ''} ${u?.lastname || ''}`.trim()
  || u?.email?.split('@')[0] || '—';
const planLabel = (u) => {
  const base = u?.subscription?.product_name || u?.plan_name || 'Free';
  return u?.expired === true ? `${base} (Expired)` : base;
};
const lastAccessedLabel = (u) => {
  if (!u?.last_accessed) return '—';
  const d = new Date(u.last_accessed);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

// V2-structured user-management panel (placement mirrors /manage-users V2), but
// styled with this page's CSS modules instead of MUI. Skeleton scope: list +
// tabs + search + Create + Access. Secondary row actions (invite / copy link /
// hide) are intentionally deferred.
const UsersPanel = ({ blockCreateUser, onCreateUserBlocked }) => {
  const navigate = useNavigate();
  const { getToken } = useContext(AuthContext);
  const {
    setAppSnackbar, switchingFunction, isTokenChange, apiResponse,
  } = useContext(AppContext);

  const [managerUsers, setManagerUsers] = useState([]);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('users'); // was 'connected' while that tab existed
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const [accessTarget, setAccessTarget] = useState(null);
  const [accessing, setAccessing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Per-row action in flight (send invite SMS / copy signup link).
  const [invitingId, setInvitingId] = useState(null);
  const [copyingUserId, setCopyingUserId] = useState(null);

  const affiliateCode = apiResponse?.data?.[0]?.affiliateCode || '';

  // `silent` refetches in the background: the table keeps showing the current
  // rows instead of collapsing into the "Loading users…" state.
  const loadUsers = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const token = await getToken();
      const [mgr, inv] = await Promise.all([
        axios.post(`${baseUrl}/managerUsersList`, { token }).catch(() => null),
        axios.post(`${baseUrl}/inviteUsersList`, { token }).catch(() => null),
      ]);
      setManagerUsers(Array.isArray(mgr?.data?.data) ? mgr.data.data : []);
      const invData = inv?.data;
      const invList = Array.isArray(invData) ? invData : (invData?.data || invData?.users || []);
      setConnectedUsers(Array.isArray(invList) ? invList : []);
    } catch (e) {
      // A failed background refresh leaves the rows on screen; only the
      // foreground load clears them.
      if (!silent) {
        setManagerUsers([]);
        setConnectedUsers([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { loadUsers(); }, [loadUsers, isTokenChange]);

  const tabs = [
    // Connected Account tab hidden for now — it lives under Viewers → Connected
    // Accounts. Uncomment to bring it back here.
    // { key: 'connected', label: 'Connected Account', count: connectedUsers.length },
    { key: 'users', label: 'Users', count: managerUsers.length },
  ];
  const baseRows = tab === 'connected' ? [...connectedUsers].reverse() : [...managerUsers].reverse();
  const q = search.trim().toLowerCase();
  const shown = baseRows.filter((u) => {
    if (!q) return true;
    const name = `${u?.firstname || ''} ${u?.lastname || ''}`.toLowerCase();
    return name.includes(q) || (u?.email || '').toLowerCase().includes(q);
  });

  const copyCode = async () => {
    const link = affiliateCode ? `https://projector.app/newSign-up?reference_code=${affiliateCode}` : '';
    if (!link) return;
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      setAppSnackbar?.({ isVisible: true, type: 'success', message: 'Affiliate signup link copied' });
    } catch (e) { /* noop */ }
  };

  // Export the current tab's (search-filtered) users to CSV — same format as
  // the /manage-users list (ManagerList.exportUsersToCSV).
  const exportUsersToCSV = () => {
    if (!shown.length) {
      setAppSnackbar?.({ isVisible: true, type: 'error', message: 'No users to export' });
      return;
    }
    const fmtDate = (v) => {
      if (!v) return '';
      const d = new Date(v);
      return Number.isNaN(d.getTime())
        ? ''
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
    const esc = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const headers = ['Name', 'Email', 'Plan', 'End Date', 'Last Accessed'];
    const rows = [headers.join(',')];
    shown.forEach((u) => {
      const name = `${u?.firstname || ''} ${u?.lastname || ''}`.trim();
      rows.push([
        esc(name),
        esc(u?.email || ''),
        esc(planLabel(u)),
        esc(fmtDate(u?.subscription?.end_date) || '-'),
        esc(fmtDate(u?.last_accessed)),
      ].join(','));
    });
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const base = tab === 'connected' ? 'connected-accounts' : 'users';
    link.download = `${base}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setAppSnackbar?.({ isVisible: true, type: 'success', message: 'User list exported successfully' });
  };

  // Create → menu with two flows: Quick user and User with a plan. When
  // create-user is gated (affiliate but membership not active), route to plans.
  const onCreateClick = () => {
    if (blockCreateUser) { onCreateUserBlocked?.(); return; }
    setMenuOpen((v) => !v);
  };
  const createUser = (userType) => {
    setMenuOpen(false);
    navigate(`/create-user-v2?type=${userType}`);
  };

  // Send the invite SMS to a created user (only before they've signed up).
  // Optimistically flips the row to "invited" (signup_status '2'), reverting on
  // failure.
  const sendInvite = async (u) => {
    if (!u?.userId) return;
    setInvitingId(u.userId);
    const setStatus = (status) => setManagerUsers((prev) => prev.map((row) => (
      row.userId === u.userId ? { ...row, signup_status: status } : row)));
    setStatus('2');
    try {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}/sendInviteLink`, { token, userId: u.userId });
      if (data?.success) {
        setAppSnackbar?.({ isVisible: true, type: 'success', message: data?.message || 'Invitation sent successfully' });
        loadUsers(true);
      } else {
        setStatus('0');
        setAppSnackbar?.({ isVisible: true, type: 'error', message: data?.message || 'Failed to send invitation' });
      }
    } catch (e) {
      setStatus('0');
      setAppSnackbar?.({ isVisible: true, type: 'error', message: e?.response?.data?.message || 'Failed to send invitation' });
    } finally {
      setInvitingId(null);
    }
  };

  // Copy the created user's signup link. Safari only allows clipboard.write()
  // synchronously inside the gesture, so the fetch is handed to ClipboardItem as
  // a promise; other browsers fall back to writeText() after the await.
  const copySignupLink = (u) => {
    if (!u?.userId) return;
    setCopyingUserId(u.userId);
    const fetchLink = (async () => {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}${endpoints.copySignupLink}`, { token, userId: u.userId });
      if (data?.success && data?.data?.short_url) return data.data.short_url;
      throw new Error(data?.message || 'Failed to get signup link');
    })();
    const ok = () => setAppSnackbar?.({ isVisible: true, type: 'success', message: 'Signup link copied to clipboard' });
    const fail = (e) => setAppSnackbar?.({ isVisible: true, type: 'error', message: e?.message || 'Failed to copy signup link' });
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      const item = new ClipboardItem({
        'text/plain': fetchLink.then((url) => new Blob([url], { type: 'text/plain' })),
      });
      navigator.clipboard.write([item]).then(ok).catch(fail).finally(() => setCopyingUserId(null));
    } else {
      fetchLink
        .then((url) => navigator.clipboard.writeText(url))
        .then(ok)
        .catch(fail)
        .finally(() => setCopyingUserId(null));
    }
  };

  const confirmAccess = async () => {
    if (!accessTarget) return;
    setAccessing(true);
    try {
      await switchingFunction?.(accessTarget);
    } catch (e) { /* switchingFunction surfaces its own errors */ } finally {
      setAccessing(false);
      setAccessTarget(null);
    }
  };

  return (
    <div className={styles.usersPanel}>
      {/* ---- Section header: affiliate code (left) + Create (right) ---- */}
      <div className={styles.usersHead}>
        <div className={styles.usersHeadMain}>
          <h2 className={styles.usersTitle}>User management</h2>
          {affiliateCode ? (
            <div className={styles.affCode}>
              <span className={styles.affCodeLabel}>Your Affiliate Code:</span>
              <span className={styles.affCodeVal}>{affiliateCode}</span>
              <button type="button" className={styles.affCopy} onClick={copyCode}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          ) : (
            <p className={styles.usersSub}>Connected accounts and the users you&apos;ve created or can access.</p>
          )}
        </div>
        <div className={styles.createWrap}>
          <button type="button" className={styles.createBtn} onClick={onCreateClick}>
            <span className={styles.createPlus}>+</span>
            Create
          </button>
          {menuOpen && (
            <>
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 19 }}
                onClick={() => setMenuOpen(false)}
              />
              <div className={styles.createMenu} role="menu">
                <button type="button" className={styles.createMenuItem} onClick={() => createUser('quick')}>
                  Quick Set Up
                </button>
                <button type="button" className={styles.createMenuItem} onClick={() => createUser('withPlan')}>
                  Full Service Set Up
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ---- Toolbar: tabs (left) + search (right) ---- */}
      <div className={styles.usersToolbar}>
        <div className={styles.tabs}>
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              <span className={styles.tabCount}>{t.count}</span>
            </button>
          ))}
        </div>
        <div className={styles.toolbarRight}>
          <input
            className={styles.usersSearch}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users"
          />
          <button
            type="button"
            className={styles.exportBtn}
            onClick={exportUsersToCSV}
            disabled={!shown.length}
            title="Export the current list as CSV"
          >
            <span className={styles.exportIcon} aria-hidden="true">↓</span>
            Export
          </button>
        </div>
      </div>

      {/* ---- Table ---- */}
      {loading ? (
        <p className={styles.usersState}>Loading users…</p>
      ) : !shown.length ? (
        <p className={styles.usersState}>
          {q ? 'No users match your search.' : `No ${tab === 'connected' ? 'connected accounts' : 'users'} yet.`}
        </p>
      ) : (
        <div className={styles.uTableWrap}>
          <table className={styles.uTable}>
            <thead>
              <tr>
                <th className={styles.uTh}>Name</th>
                <th className={styles.uTh}>Plan</th>
                <th className={styles.uTh}>Last accessed</th>
                <th className={`${styles.uTh} ${styles.uThRight}`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((u, idx) => {
                const name = rowName(u);
                const expired = u?.expired === true;
                const canAccess = Boolean(u?.userId && u?.token);
                // Invite SMS + copy signup link only make sense for a user I
                // created who hasn't signed up yet ('0' = not invited, '2' = invited).
                const canInvite = tab === 'users' && Boolean(u?.userId)
                  && (u?.signup_status === '0' || u?.signup_status === '2');
                return (
                  <tr key={u?.userId || u?.email || idx} className={styles.uTr}>
                    <td className={styles.uTd}>
                      <div className={styles.uNameCell}>
                        <span className={styles.uAvatar}>
                          {u?.dpURL || u?.image ? (
                            <img src={u.dpURL || u.image} alt={name} />
                          ) : name.charAt(0).toUpperCase()}
                        </span>
                        <span className={styles.uNameText}>
                          <span className={styles.uName}>{name}</span>
                          <span className={styles.uEmail}>{u?.email}</span>
                        </span>
                      </div>
                    </td>
                    <td className={styles.uTd}>
                      <span className={`${styles.planChip} ${expired ? styles.planChipExpired : ''}`}>
                        {planLabel(u)}
                      </span>
                    </td>
                    <td className={`${styles.uTd} ${styles.uMuted}`}>{lastAccessedLabel(u)}</td>
                    <td className={`${styles.uTd} ${styles.uTdRight}`}>
                      <div className={styles.uRowActions}>
                        {canInvite && (
                          <>
                            <button
                              type="button"
                              className={styles.uRowActBtn}
                              onClick={() => sendInvite(u)}
                              disabled={invitingId === u.userId}
                            >
                              {/* eslint-disable-next-line no-nested-ternary */}
                              {invitingId === u.userId ? 'Sending…' : (u?.signup_status === '2' ? 'Resend Email' : 'Send Email')}
                            </button>
                            <button
                              type="button"
                              className={styles.uRowActBtn}
                              onClick={() => copySignupLink(u)}
                              disabled={copyingUserId === u.userId}
                            >
                              {copyingUserId === u.userId ? 'Copying…' : 'Copy link'}
                            </button>
                          </>
                        )}
                        {canAccess ? (
                          <button type="button" className={styles.accessBtn} onClick={() => setAccessTarget(u)}>
                            Access
                          </button>
                        ) : (!canInvite && <span className={styles.uMuted}>—</span>)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- Access confirm ---- */}
      {accessTarget && (
        <div className={styles.modalOverlay} onClick={() => { if (!accessing) setAccessTarget(null); }}>
          <div className={styles.confirmCard} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalBody}>
              <h3 className={styles.confirmTitle}>{`Access ${rowName(accessTarget)}'s account?`}</h3>
              <p className={styles.confirmText}>
                You&apos;ll switch into this account and act on their behalf until you exit.
              </p>
              <div className={styles.confirmActions}>
                <button type="button" className={styles.coSecondary} onClick={() => setAccessTarget(null)} disabled={accessing}>
                  Cancel
                </button>
                <button type="button" className={styles.gateModalBtn} onClick={confirmAccess} disabled={accessing}>
                  {accessing ? 'Accessing…' : 'Access account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPanel;
